import React, { useEffect } from 'react';
import Layout from 'components/layout';
import HeadData, { Props as HeadDataProps } from 'components/head-data';
import ListingPage from 'components/dynamic-page/listing-page';
import listingPageProps from 'components/dynamic-page/listing-page/get-server-side-props';
import OffMarketPage from 'components/dynamic-page/off-market-page';
import createListing from 'data/listing';
import { createGetServerSideProps } from 'utils/shared-getServerSideProps';
import ErrorState from 'components/error-state';
import { useRouter } from 'next/router';
import { AbortReasons } from 'constants/abort-reasons';
import { trackEvent } from 'utils/google-tag-manager';
import { GTM_LISTING_PAGE_LOAD_UI_ERROR, GTM_LISTING_PAGE_TIMEOUT_UI_ERROR } from 'constants/events';
import { CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE } from 'constants/headers';
import { LISTING_PAGE } from 'constants/errorMessages';

import type { GetServerSidePropsContext } from 'next';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import { ExperimentVariants, type PaginationParams } from 'utils/types';
import type { Breadcrumb } from 'components/breadcrumbs';
import type { LinkDataType } from 'components/home-page/internal-links';
import type Address from 'data/addresses';
import { LISTING_ROUTE } from 'components/dynamic-page/route-matchers';
import { useExperiment } from '@growthbook/growthbook-react';
import { Stats } from 'data/stats';
import { Zoopraisal as ZoopraisalData } from 'utils/zoopraisal';
import School from 'data/schools';
import Neighbourhood from 'data/neighbourhood';
import { ThemeNames } from 'types/themes';

function trackGtmListingPageError(reason?: string) {
  if (!reason) return;
  switch (reason) {
  case AbortReasons.TIMEOUT:
    trackEvent(GTM_LISTING_PAGE_TIMEOUT_UI_ERROR);
    break;
  case AbortReasons.MANUAL:
  case AbortReasons.ON_UNMOUNT:
    break;
  default:
    console.warn(`Cannot load listing page due to unexpected reason: ${reason}`);
    trackEvent(GTM_LISTING_PAGE_LOAD_UI_ERROR);
  }
}

export const getServerSideProps = createGetServerSideProps(async (context: GetServerSidePropsContext) => {
  context.res.setHeader(CACHE_CONTROL_HEADER_NAME, CACHE_CONTROL_HEADER_VALUE);
  const props = await listingPageProps(context);
  return {
    props: {
      ...props,
    },
  };
});

interface HeadTags {
  headTags: HeadDataProps['data'];
}

export interface DynamicPageProps extends HeadTags {
  listingData: Record<string, unknown>;
  listingsData: Record<string, unknown>[];
  breadcrumbs: Breadcrumb[];
  params: ListingParams & PaginationParams;
  pageNumber: number;
  totalCount: number;
  totalPages: number;
  isCrawler: boolean;
  isCrawlerLighthouse: boolean;
  footerData: LinkDataType[];
  addressesData: Address[];
  errorReason?: string;
  chartData: Stats | null;
  neighbourhoodData: Stats | null;
  zoopraisalData: ZoopraisalData | null;
  demographics: {
    schools: Array<Record<string, unknown>>;
    neighbourhood: Record<string, unknown> | null;
  };
  theme: string;
}

interface Props {
  props: DynamicPageProps;
}

export default function DynamicListingPage({ props }: Props) {
  useEffect(() => {
    // Run on client-side to call GTM without fail
    try {
      trackGtmListingPageError(props?.errorReason);
    } catch (error) {
      console.error(error);
    }
  }, [props]);

  if (props?.errorReason) {
    return (
      <Layout>
        <ErrorState
          image={{
            src: '/next/assets/images/not-found-error.svg',
            altText: 'Not Found Error',
          }}
          title={LISTING_PAGE.ERR_LISTING_NOT_FOUND}
          description={LISTING_PAGE.ERR_LISTING_NOT_FOUND_DESCRIPTION}
        />
      </Layout>);
  }

  const { headTags, isCrawler, isCrawlerLighthouse, footerData, addressesData, chartData, neighbourhoodData, zoopraisalData, demographics, theme } = props;
  const listing = new createListing(props.listingData);
  const listings = props.listingsData
    .map(i => new createListing(i))
    .sort((a, b) => a.addedAt < b.addedAt ? 1 : -1); //sort descending for price history component
  const breadcrumbs = props.breadcrumbs;

  const abTestingFavouriteToggle = useExperiment({ key: 'listing-favourite-toggle', variations: [0, 1]});
  const isFavouriteToggleExperiment = theme === ThemeNames.ZOOCASA && abTestingFavouriteToggle.variationId === ExperimentVariants.B;
  return (
    <Layout showMobileBottomNav={false} routeName={LISTING_ROUTE}>
      {headTags && !listing.isOffMarket && <HeadData data={headTags} />}
      {listing.isOffMarket
          && <HeadData data={{ title: headTags?.title, schema: headTags?.schema, canonicalUrl: headTags?.canonicalUrl,
            ogUrl: headTags?.ogUrl, metaDescription: headTags?.metaDescription, hrefLangs: headTags?.hrefLangs }} />}
      {listing.isOffMarket && <OffMarketPage listing={listing} listings={listings} breadcrumbs={breadcrumbs} isCrawler={isCrawler} />}
      {!listing.isOffMarket && <ListingPage listing={listing} listings={listings} breadcrumbs={breadcrumbs} isCrawler={isCrawler} footerData={footerData} addressesData={addressesData} isCrawlerLighthouse={isCrawlerLighthouse} isFavouriteToggleExperiment={isFavouriteToggleExperiment} chartData={chartData} neighbourhoodData={neighbourhoodData} zoopraisalDataPramas={zoopraisalData} demographics={demographics} />}
    </Layout>
  );
}
